import {
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { type KeyboardEvent, useContext, useState } from 'react';
import { useSendPasswordResetEmail } from 'react-firebase-hooks/auth';
import type { FirebaseError } from 'firebase/app';
import { getAuthErrorMessage } from '@/helpers';
import { buildAuthContinueUrl } from '@/helpers/auth/authUrls';
import { FirebaseContext } from '@/session/FirebaseContext';
import {
  AuthHeading,
  AuthSubtext,
  AuthTextButton,
  EmailDeliveryHint,
  PrimaryAuthButton,
} from './auth-ui';
import { BaseAuthCard } from './base-card';

export function ForgotPasswordCard(props: { triggerSignup: () => void; triggerLogin: () => void }) {
  const { auth } = useContext(FirebaseContext);

  const [email, setEmail] = useState<string>('');
  const [sendPasswordResetEmail, sending, error] = useSendPasswordResetEmail(auth);
  const [emailSent, setEmailSent] = useState<boolean>(false);

  const actionCodeSettings = {
    url: buildAuthContinueUrl('/reset-password'),
    handleCodeInApp: true,
  };

  const handleEmail = async () => {
    setEmailSent(false);
    const success = await sendPasswordResetEmail(email, actionCodeSettings);
    if (success) {
      setEmailSent(true);
    }
  };

  const showError = error && (error as FirebaseError).code === 'auth/too-many-requests';

  return (
    <BaseAuthCard>
      <AuthHeading>Recover your password</AuthHeading>
      <AuthSubtext mb={4}>
        Enter your email and we&apos;ll send reset instructions if an account exists.
      </AuthSubtext>
      <VStack align='stretch' spacing={4}>
        <FormControl>
          <FormLabel fontSize='sm' fontWeight='medium' mb={1}>
            Email
          </FormLabel>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type='email'
            autoComplete='email'
            size='md'
            borderRadius='md'
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') handleEmail();
            }}
          />
        </FormControl>
        <PrimaryAuthButton isDisabled={emailSent} isLoading={sending} onClick={handleEmail}>
          {emailSent ? 'Email sent' : 'Send recovery email'}
        </PrimaryAuthButton>

        {emailSent && (
          <>
            <AuthSubtext textAlign='center'>
              If an account exists for this email, 10 Teams sent password reset instructions.
            </AuthSubtext>
            <EmailDeliveryHint />
          </>
        )}

        {showError && (
          <Text color='red.500' fontSize='sm' textAlign='center'>
            {getAuthErrorMessage(error as FirebaseError)}
          </Text>
        )}

        <AuthTextButton width='full' onClick={props.triggerLogin}>
          Back to log in
        </AuthTextButton>
      </VStack>
    </BaseAuthCard>
  );
}
